import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core'
import { ConfirmationService, MessageService } from 'primeng/api'

import { ApiRiskScorecardConfigService } from '../../data/api-risk-scorecard-config.service'
import { STATUS_UI, personName } from '../../data/risk-scorecard-config.labels'
import { ConfigVersion } from '../../data/risk-scorecard-config.interface'
import { AuthService } from '@data/services/modules/auth/auth.service'

/**
 * Versiones de la configuración de la calculadora de riesgo (plan v4.2 §7.2). Admin y Finanzas crean y activan
 * (D-17); solo Admin borra borradores. Activar se hace desde el detalle, siempre con motivo.
 */
@Component({
  selector: 'app-risk-scorecard-config-list',
  templateUrl: './risk-scorecard-config-list.component.html',
  styleUrls: ['./risk-scorecard-config-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, MessageService],
})
export class RiskScorecardConfigListComponent implements OnInit {
  versions: ConfigVersion[] = []
  loading = false
  loadError: string | null = null
  //* El motivo de la última activación de la versión en uso, que no viene en la lista. `undefined` mientras carga,
  //* para no mostrar antes el motivo con que se creó la versión, que es otro.
  activeReason: string | null | undefined = undefined

  readonly statusUi = STATUS_UI
  readonly personName = personName
  readonly canCreate: boolean
  readonly canDelete: boolean

  private readonly api = inject(ApiRiskScorecardConfigService)
  private readonly confirm = inject(ConfirmationService)
  private readonly messages = inject(MessageService)
  private readonly cd = inject(ChangeDetectorRef)

  constructor() {
    const auth = inject(AuthService)
    this.canCreate = auth.hasModulePermission('risk-scorecard-config', 'create')
    this.canDelete = auth.hasModulePermission('risk-scorecard-config', 'delete')
  }

  get active(): ConfigVersion | null {
    return this.versions.find((version) => version.status === 'active') ?? null
  }

  ngOnInit(): void {
    this.load()
  }

  load(): void {
    this.loading = true
    this.loadError = null
    this.api.list().subscribe((response) => {
      this.loading = false
      if (response.error) {
        this.versions = []
        this.loadError = response.status === 403 ? 'No tienes permiso para ver la configuración de la calculadora.' : response.msg
      } else this.versions = response.data ?? []
      this.loadActiveReason()
      this.cd.markForCheck()
    })
  }

  /** La semilla se activó sin motivo propio: entonces se muestra el de la versión. */
  private loadActiveReason(): void {
    const current = this.active
    this.activeReason = undefined
    if (!current) return
    this.api.get(current._id).subscribe((response) => {
      this.activeReason = (!response.error && response.data?.activations?.[0]?.reason) || current.changeReason || null
      this.cd.markForCheck()
    })
  }

  /** Quién y cuándo: el que la activó si está o estuvo activa; si no, quien la creó. */
  authorOf(version: ConfigVersion): string {
    return personName(version.activatedBy ?? version.createdBy)
  }

  dateOf(version: ConfigVersion): string | null {
    return version.activatedAt ?? version.createdAt
  }

  canRemove(version: ConfigVersion): boolean {
    return this.canDelete && version.status === 'draft' && !version.isSystemDefault
  }

  remove(version: ConfigVersion): void {
    if (!this.canRemove(version)) return
    this.confirm.confirm({
      key: 'risk-config-list',
      header: `Borrar el borrador v${version.version}`,
      message: `«${version.name}» se borra por completo. Nadie lo ha usado todavía.`,
      acceptLabel: 'Borrar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () =>
        this.api.remove(version._id).subscribe((response) => {
          if (response.error) this.messages.add({ key: 'risk-config-list', severity: 'error', summary: 'No se borró', detail: response.msg, life: 6000 })
          else this.messages.add({ key: 'risk-config-list', severity: 'success', summary: 'Borrador borrado', detail: `La versión ${version.version} ya no existe.`, life: 4000 })
          this.load()
        }),
    })
  }

  trackById(_index: number, version: ConfigVersion): string {
    return version._id
  }
}
